import React, { useState, useEffect } from 'react';
import { ArrowLeft, Thermometer, Trophy, HelpCircle, RotateCcw, Cloud, Sun, Snowflake } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { GameMode, GameStats, Language } from '../types';
import useSound from 'use-sound';
import successSound from '../assets/sounds/success.wav';
import errorSound from '../assets/sounds/error2.mp3';

interface WeatherChallengeGameProps {
  mode: GameMode;
  onReset: () => void;
  language: Language;
}

interface WeatherChallenge {
  id: number;
  type: 'read_temperature' | 'dress_character' | 'plan_activity';
  temperature: number;
  description: string;
  correctAnswers: string[];
  difficulty: number;
  season: 'spring' | 'summer' | 'autumn' | 'winter';
}

interface ClothingItem {
  id: number;
  name: string;
  type: 'top' | 'bottom' | 'accessory';
  minTemp: number;
  maxTemp: number;
  icon: string;
}

interface Activity {
  id: number;
  name: string;
  minTemp: number;
  maxTemp: number;
  icon: string;
}

const WeatherChallengeGame: React.FC<WeatherChallengeGameProps> = ({ mode, onReset, language }) => {
  const [level, setLevel] = useState(1);
  const [currentChallenge, setCurrentChallenge] = useState<WeatherChallenge | null>(null);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [score, setScore] = useState(0);
  const [challengeCount, setChallengeCount] = useState(0);
  const [gameComplete, setGameComplete] = useState(false);
  const [showTutorial, setShowTutorial] = useState(true);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [stats, setStats] = useState<GameStats>({
    questionsAnswered: 0,
    correctAnswers: 0,
    incorrectAnswers: 0,
    totalTime: 0,
    averageResponseTime: 0,
  });

  const [playSuccess] = useSound(successSound, { volume: 0.5 });
  const [playError] = useSound(errorSound, { volume: 0.3 });

  const tutorialSteps = [
    {
      title: language === 'en' ? 'Welcome to Weather Challenge!' : 'Bienvenue au Défi Météo !',
      content: language === 'en' 
        ? 'Learn to read temperatures and make weather-appropriate choices! Help our character dress for the weather.'
        : 'Apprenez à lire les températures et faire des choix adaptés à la météo ! Aidez notre personnage à s\'habiller selon le temps.'
    },
    {
      title: language === 'en' ? 'Reading Thermometers' : 'Lire les thermomètres',
      content: language === 'en'
        ? 'Temperature is measured in degrees Celsius (°C). 0°C is freezing, 20°C is comfortable, 30°C is hot!'
        : 'La température se mesure en degrés Celsius (°C). 0°C c\'est le gel, 20°C c\'est agréable, 30°C c\'est chaud !'
    },
    {
      title: language === 'en' ? 'Weather Wisdom' : 'Sagesse météorologique',
      content: language === 'en'
        ? 'Choose clothes and activities that match the temperature. Cold weather needs warm clothes, hot weather needs light clothes!'
        : 'Choisissez des vêtements et activités qui correspondent à la température. Temps froid = vêtements chauds, temps chaud = vêtements légers !'
    }
  ];

  const clothingItems: ClothingItem[] = [
    { id: 1, name: language === 'en' ? 'T-shirt' : 'T-shirt', type: 'top', minTemp: 15, maxTemp: 35, icon: '👕' },
    { id: 2, name: language === 'en' ? 'Sweater' : 'Pull', type: 'top', minTemp: 5, maxTemp: 20, icon: '🧥' },
    { id: 3, name: language === 'en' ? 'Winter Coat' : 'Manteau d\'hiver', type: 'top', minTemp: -10, maxTemp: 10, icon: '🧥' },
    { id: 4, name: language === 'en' ? 'Shorts' : 'Short', type: 'bottom', minTemp: 20, maxTemp: 35, icon: '🩳' },
    { id: 5, name: language === 'en' ? 'Pants' : 'Pantalon', type: 'bottom', minTemp: 0, maxTemp: 25, icon: '👖' },
    { id: 6, name: language === 'en' ? 'Hat' : 'Bonnet', type: 'accessory', minTemp: -10, maxTemp: 5, icon: '🧢' },
    { id: 7, name: language === 'en' ? 'Sunglasses' : 'Lunettes de soleil', type: 'accessory', minTemp: 20, maxTemp: 35, icon: '🕶️' },
    { id: 8, name: language === 'en' ? 'Scarf' : 'Écharpe', type: 'accessory', minTemp: -10, maxTemp: 10, icon: '🧣' },
  ];

  const activities: Activity[] = [
    { id: 1, name: language === 'en' ? 'Swimming' : 'Natation', minTemp: 25, maxTemp: 35, icon: '🏊' },
    { id: 2, name: language === 'en' ? 'Skiing' : 'Ski', minTemp: -10, maxTemp: 5, icon: '⛷️' },
    { id: 3, name: language === 'en' ? 'Picnic' : 'Pique-nique', minTemp: 15, maxTemp: 30, icon: '🧺' },
    { id: 4, name: language === 'en' ? 'Ice Skating' : 'Patinage', minTemp: -10, maxTemp: 0, icon: '⛸️' },
    { id: 5, name: language === 'en' ? 'Beach Day' : 'Journée plage', minTemp: 22, maxTemp: 35, icon: '🏖️' },
    { id: 6, name: language === 'en' ? 'Hot Chocolate' : 'Chocolat chaud', minTemp: -10, maxTemp: 10, icon: '☕' },
  ];

  const generateChallenge = (): WeatherChallenge => {
    const temperatures = {
      1: [-5, 0, 5, 15, 20, 25], // Niveau 1 - Températures simples
      2: [-8, 3, 12, 18, 28, 32], // Niveau 2 - Plus de variété
      3: [-10, -2, 8, 16, 24, 35] // Niveau 3 - Extrêmes
    };

    const levelTemps = temperatures[level as keyof typeof temperatures];
    const temperature = levelTemps[Math.floor(Math.random() * levelTemps.length)];
    
    const challengeTypes = level === 1 ? ['read_temperature'] : 
                          level === 2 ? ['read_temperature', 'dress_character'] : 
                          ['read_temperature', 'dress_character', 'plan_activity'];
    
    const type = challengeTypes[Math.floor(Math.random() * challengeTypes.length)] as WeatherChallenge['type'];
    
    let season: WeatherChallenge['season'];
    if (temperature <= 5) season = 'winter';
    else if (temperature <= 15) season = 'autumn';
    else if (temperature <= 25) season = 'spring';
    else season = 'summer';

    switch (type) {
      case 'read_temperature':
        return {
          id: 1,
          type,
          temperature,
          description: language === 'en' 
            ? `What temperature is shown on the thermometer?`
            : `Quelle température indique le thermomètre ?`,
          correctAnswers: [`${temperature}°C`],
          difficulty: 1,
          season
        };

      case 'dress_character':
        const appropriateClothing = clothingItems.filter(item => 
          temperature >= item.minTemp && temperature <= item.maxTemp
        );
        return {
          id: 2,
          type,
          temperature,
          description: language === 'en'
            ? `It's ${temperature}°C outside. How should our character dress?`
            : `Il fait ${temperature}°C dehors. Comment notre personnage devrait-il s'habiller ?`,
          correctAnswers: appropriateClothing.map(item => item.name),
          difficulty: 2,
          season
        };

      case 'plan_activity':
        const appropriateActivities = activities.filter(activity => 
          temperature >= activity.minTemp && temperature <= activity.maxTemp
        );
        return {
          id: 3,
          type,
          temperature,
          description: language === 'en'
            ? `It's ${temperature}°C. What activities are suitable for this weather?`
            : `Il fait ${temperature}°C. Quelles activités conviennent à ce temps ?`,
          correctAnswers: appropriateActivities.map(activity => activity.name),
          difficulty: 3,
          season
        };

      default:
        return generateChallenge();
    }
  };

  const getThermometerHeight = (temp: number) => {
    // Convertir la température en pourcentage de hauteur (de -10°C à 35°C)
    const minTemp = -10;
    const maxTemp = 35;
    const percentage = ((temp - minTemp) / (maxTemp - minTemp)) * 100;
    return Math.max(0, Math.min(100, percentage));
  };

  const getTemperatureColor = (temp: number) => {
    if (temp <= 0) return '#3B82F6'; // Bleu pour le froid
    if (temp <= 10) return '#06B6D4'; // Cyan
    if (temp <= 20) return '#10B981'; // Vert
    if (temp <= 30) return '#F59E0B'; // Orange
    return '#EF4444'; // Rouge pour le chaud
  };

  const getWeatherIcon = () => {
    if (!currentChallenge) return <Sun className="h-8 w-8" />;
    
    const temp = currentChallenge.temperature;
    if (temp <= 0) return <Snowflake className="h-8 w-8 text-blue-500" />;
    if (temp <= 15) return <Cloud className="h-8 w-8 text-gray-500" />;
    return <Sun className="h-8 w-8 text-yellow-500" />;
  };

  const handleItemSelect = (item: string) => {
    if (selectedItems.includes(item)) {
      setSelectedItems(selectedItems.filter(i => i !== item));
    } else {
      setSelectedItems([...selectedItems, item]);
    }
  };

  const checkChallenge = () => {
    if (!currentChallenge) return;

    let correct = false;

    switch (currentChallenge.type) {
      case 'read_temperature':
        correct = selectedItems.includes(`${currentChallenge.temperature}°C`);
        break;
      case 'dress_character':
      case 'plan_activity':
        // Vérifier qu'au moins un élément correct est sélectionné et aucun incorrect
        const hasCorrectItem = selectedItems.some(item => currentChallenge.correctAnswers.includes(item));
        const hasIncorrectItem = selectedItems.some(item => !currentChallenge.correctAnswers.includes(item));
        correct = hasCorrectItem && !hasIncorrectItem && selectedItems.length > 0;
        break;
    }

    setIsCorrect(correct);
    setShowFeedback(true);

    const newStats = {
      ...stats,
      questionsAnswered: stats.questionsAnswered + 1,
      correctAnswers: stats.correctAnswers + (correct ? 1 : 0),
      incorrectAnswers: stats.incorrectAnswers + (correct ? 0 : 1),
    };
    setStats(newStats);

    if (correct) {
      const points = currentChallenge.difficulty * 10;
      setScore(score + points);
      playSuccess();
    } else {
      playError();
    }

    setTimeout(() => {
      setChallengeCount(challengeCount + 1);
      if (challengeCount >= 9) {
        setGameComplete(true);
        saveProgress(newStats);
      } else {
        if (challengeCount % 3 === 2 && level < 3) {
          setLevel(level + 1);
        }
        nextChallenge();
      }
    }, 2000);
  };

  const nextChallenge = () => {
    setCurrentChallenge(generateChallenge());
    setSelectedItems([]);
    setShowFeedback(false);
  };

  const saveProgress = async (currentStats: GameStats) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase.from('user_progress').insert({
        user_id: user.id,
        operation: 'measures',
        mode: 'weather',
        questions_answered: currentStats.questionsAnswered,
        correct_answers: currentStats.correctAnswers,
        average_response_time: 0,
      });
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  };

  const resetGame = () => {
    setLevel(1);
    setScore(0);
    setChallengeCount(0);
    setGameComplete(false);
    setShowTutorial(true);
    setTutorialStep(0);
    setStats({
      questionsAnswered: 0,
      correctAnswers: 0,
      incorrectAnswers: 0,
      totalTime: 0,
      averageResponseTime: 0,
    });
    nextChallenge();
  };

  useEffect(() => {
    if (!showTutorial) {
      nextChallenge();
    }
  }, [showTutorial]);

  if (showTutorial) {
    return (
      <div className="w-full max-w-4xl">
        <div className="mb-6 flex items-center">
          <button
            onClick={onReset}
            className="flex items-center text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors"
          >
            <ArrowLeft className="h-5 w-5 mr-1" />
            {language === 'en' ? 'Back to Modes' : 'Retour aux modes'}
          </button>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-8 text-center">
          <Thermometer className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
            {tutorialSteps[tutorialStep].title}
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-300 mb-8">
            {tutorialSteps[tutorialStep].content}
          </p>
          
          <div className="flex justify-center gap-4">
            {tutorialStep > 0 && (
              <button
                onClick={() => setTutorialStep(tutorialStep - 1)}
                className="px-6 py-3 bg-slate-600 text-white rounded-lg hover:bg-slate-700"
              >
                {language === 'en' ? 'Previous' : 'Précédent'}
              </button>
            )}
            
            {tutorialStep < tutorialSteps.length - 1 ? (
              <button
                onClick={() => setTutorialStep(tutorialStep + 1)}
                className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                {language === 'en' ? 'Next' : 'Suivant'}
              </button>
            ) : (
              <button
                onClick={() => setShowTutorial(false)}
                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                {language === 'en' ? 'Start Challenge!' : 'Commencer le défi !'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-6xl">
      <div className="mb-6 flex items-center justify-between">
        <button
          onClick={onReset}
          className="flex items-center text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 mr-1" />
          {language === 'en' ? 'Back to Modes' : 'Retour aux modes'}
        </button>
        
        <div className="flex items-center gap-4">
          <div className="text-lg font-medium text-red-700 dark:text-red-300">
            {language === 'en' ? `Level ${level}` : `Niveau ${level}`} - {language === 'en' ? `Challenge ${challengeCount + 1}/10` : `Défi ${challengeCount + 1}/10`}
          </div>
          
          <button
            onClick={() => setShowTutorial(true)}
            className="p-2 text-slate-600 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400"
          >
            <HelpCircle className="h-5 w-5" />
          </button>
        </div>
      </div>

      {gameComplete ? (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-8 text-center">
          <Trophy className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
            {language === 'en' ? 'Weather Expert!' : 'Expert Météo !'}
          </h2>
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-slate-50 dark:bg-slate-700 p-4 rounded-lg">
              <div className="text-3xl font-bold text-red-600 dark:text-red-400">{score}</div>
              <div className="text-slate-600 dark:text-slate-300">
                {language === 'en' ? 'Total Points' : 'Points totaux'}
              </div>
            </div>
            <div className="bg-slate-50 dark:bg-slate-700 p-4 rounded-lg">
              <div className="text-3xl font-bold text-red-600 dark:text-red-400">
                {Math.round((stats.correctAnswers / stats.questionsAnswered) * 100)}%
              </div>
              <div className="text-slate-600 dark:text-slate-300">
                {language === 'en' ? 'Accuracy' : 'Précision'}
              </div>
            </div>
          </div>
          <button
            onClick={resetGame}
            className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            {language === 'en' ? 'Forecast Again' : 'Prévoir à nouveau'}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Thermometer Panel */}
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6">
            <div className="flex items-center gap-2 mb-4">
              <Thermometer className="h-5 w-5 text-red-600 dark:text-red-400" />
              <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
                {language === 'en' ? 'Thermometer' : 'Thermomètre'}
              </h3>
            </div>
            
            {currentChallenge && (
              <div className="space-y-4">
                {/* Weather Icon */}
                <div className="text-center">
                  {getWeatherIcon()}
                </div>
                
                {/* Thermometer Visualization */}
                <div className="flex justify-center">
                  <div className="relative">
                    {/* Thermometer Body */}
                    <div className="w-8 h-64 bg-slate-200 dark:bg-slate-700 rounded-full relative overflow-hidden">
                      {/* Temperature Fill */}
                      <div 
                        className="absolute bottom-0 w-full rounded-full transition-all duration-1000"
                        style={{ 
                          height: `${getThermometerHeight(currentChallenge.temperature)}%`,
                          backgroundColor: getTemperatureColor(currentChallenge.temperature)
                        }}
                      />
                      
                      {/* Bulb */}
                      <div 
                        className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-12 h-12 rounded-full"
                        style={{ backgroundColor: getTemperatureColor(currentChallenge.temperature) }}
                      />
                    </div>
                    
                    {/* Temperature Scale */}
                    <div className="absolute -right-8 top-0 h-full flex flex-col justify-between text-xs text-slate-600 dark:text-slate-400">
                      <span>35°C</span>
                      <span>30°C</span>
                      <span>25°C</span>
                      <span>20°C</span>
                      <span>15°C</span>
                      <span>10°C</span>
                      <span>5°C</span>
                      <span>0°C</span>
                      <span>-5°C</span>
                      <span>-10°C</span>
                    </div>
                  </div>
                </div>
                
                {/* Digital Display */}
                <div className="bg-black rounded-lg p-4 text-center">
                  <div className="text-green-400 font-mono text-2xl">
                    {currentChallenge.type === 'read_temperature' ? '??°C' : `${currentChallenge.temperature}°C`}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Challenge Panel */}
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
                {language === 'en' ? 'Challenge' : 'Défi'}
              </h3>
              <button
                onClick={() => setSelectedItems([])}
                className="p-2 text-slate-600 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400"
              >
                <RotateCcw className="h-4 w-4" />
              </button>
            </div>
            
            {currentChallenge && (
              <div className="space-y-4">
                <div className="bg-red-50 dark:bg-red-900/30 p-4 rounded-lg">
                  <p className="text-red-800 dark:text-red-200 font-medium">
                    {currentChallenge.description}
                  </p>
                </div>
                
                {/* Temperature Options for reading challenge */}
                {currentChallenge.type === 'read_temperature' && (
                  <div className="grid grid-cols-3 gap-2">
                    {[-5, 0, 5, 10, 15, 20, 25, 30, 35].map((temp) => (
                      <button
                        key={temp}
                        onClick={() => handleItemSelect(`${temp}°C`)}
                        className={`p-3 rounded-lg border-2 transition-colors ${
                          selectedItems.includes(`${temp}°C`)
                            ? 'border-red-500 bg-red-50 dark:bg-red-900/50'
                            : 'border-slate-200 dark:border-slate-700 hover:border-red-300'
                        }`}
                      >
                        {temp}°C
                      </button>
                    ))}
                  </div>
                )}
                
                {/* Clothing Options */}
                {currentChallenge.type === 'dress_character' && (
                  <div className="space-y-3">
                    {clothingItems.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => handleItemSelect(item.name)}
                        className={`w-full p-3 rounded-lg border-2 transition-colors flex items-center gap-3 ${
                          selectedItems.includes(item.name)
                            ? 'border-red-500 bg-red-50 dark:bg-red-900/50'
                            : 'border-slate-200 dark:border-slate-700 hover:border-red-300'
                        }`}
                      >
                        <span className="text-2xl">{item.icon}</span>
                        <span className="text-slate-700 dark:text-slate-300">{item.name}</span>
                      </button>
                    ))}
                  </div>
                )}
                
                {/* Activity Options */}
                {currentChallenge.type === 'plan_activity' && (
                  <div className="space-y-3">
                    {activities.map((activity) => (
                      <button
                        key={activity.id}
                        onClick={() => handleItemSelect(activity.name)}
                        className={`w-full p-3 rounded-lg border-2 transition-colors flex items-center gap-3 ${
                          selectedItems.includes(activity.name)
                            ? 'border-red-500 bg-red-50 dark:bg-red-900/50'
                            : 'border-slate-200 dark:border-slate-700 hover:border-red-300'
                        }`}
                      >
                        <span className="text-2xl">{activity.icon}</span>
                        <span className="text-slate-700 dark:text-slate-300">{activity.name}</span>
                      </button>
                    ))}
                  </div>
                )}
                
                <button
                  onClick={checkChallenge}
                  disabled={selectedItems.length === 0 || showFeedback}
                  className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {language === 'en' ? 'Check Answer' : 'Vérifier la réponse'}
                </button>

                {showFeedback && (
                  <div className={`p-3 rounded-lg ${
                    isCorrect 
                      ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300'
                      : 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300'
                  }`}>
                    <p className="font-medium text-center">
                      {isCorrect 
                        ? (language === 'en' ? 'Perfect Weather Choice! 🌤️' : 'Choix météo parfait ! 🌤️')
                        : (language === 'en' ? 'Try Again! 🌡️' : 'Réessayez ! 🌡️')}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Character & Info Panel */}
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6">
            <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4">
              {language === 'en' ? 'Weather Info' : 'Info Météo'}
            </h3>
            
            {/* Character Visualization */}
            <div className="text-center mb-6">
              <div className="text-6xl mb-2">🧑</div>
              <div className="text-sm text-slate-600 dark:text-slate-400">
                {language === 'en' ? 'Help me dress for the weather!' : 'Aidez-moi à m\'habiller pour le temps !'}
              </div>
            </div>
            
            {/* Selected Items */}
            <div className="space-y-2 mb-6">
              <h4 className="font-medium text-slate-700 dark:text-slate-300">
                {language === 'en' ? 'Selected:' : 'Sélectionné :'}
              </h4>
              <div className="min-h-[60px] p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
                {selectedItems.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {selectedItems.map((item, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 rounded text-sm"
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className="text-slate-400 dark:text-slate-500 text-sm">
                    {language === 'en' ? 'No items selected' : 'Aucun élément sélectionné'}
                  </div>
                )}
              </div>
            </div>
            
            {/* Temperature Guide */}
            <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4">
              <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">
                {language === 'en' ? 'Temperature Guide:' : 'Guide des températures :'}
              </h4>
              <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
                <li>• {language === 'en' ? 'Below 0°C: Very cold, winter clothes' : 'Moins de 0°C : Très froid, vêtements d\'hiver'}</li>
                <li>• {language === 'en' ? '0-15°C: Cool, warm clothes' : '0-15°C : Frais, vêtements chauds'}</li>
                <li>• {language === 'en' ? '15-25°C: Comfortable, light clothes' : '15-25°C : Agréable, vêtements légers'}</li>
                <li>• {language === 'en' ? 'Above 25°C: Hot, summer clothes' : 'Plus de 25°C : Chaud, vêtements d\'été'}</li>
              </ul>
            </div>
            
            <div className="mt-4 text-center text-sm text-slate-500 dark:text-slate-400">
              {language === 'en' ? 'Score:' : 'Score :'} {score}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WeatherChallengeGame;